import { FC, useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Autocomplete,
  TextField,
  InputAdornment,
  CircularProgress,
  Divider,
  Tooltip,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useAppSelector } from '@/store';
import { useGetAverageUnitCostQuery } from '@/services';
import { useGetHTSCodesQuery } from '@/services/dashboard/sm-api';
import { useHTSDescriptions } from '@/context/hts-descriptions-context';
import { formatHTSCode, formatNumberWithSuffix } from '@/utils';
import { roles } from '@/roles';
import { PlaceholderCard } from '@/components';
import { tariff_code_placeholder } from '@/constants';
import UnitCostAnalysisSection from '../product-catalogue/components/duty-calculator/UnitCostAnalysisSection';

interface HtsOption {
  hts_code: string;
  description?: string;
}

interface ClientHtsOption {
  hts_code: string;
  goods_value: number;
  duty_value?: number;
  formatted_hts_code: string;
}

type AnyHtsOption = HtsOption | ClientHtsOption;

const isClientHtsOption = (option: AnyHtsOption): option is ClientHtsOption => {
  return 'goods_value' in option;
};

const normalizeHtsCode = (code: string): string => {
  const normalized = code.replace(/\./g, '').replace(/^0+/, '');
  return normalized || code.replace(/\./g, '');
};

const UnitCostAnalysisView: FC = () => {
  const [htsSearchValue, setHtsSearchValue] = useState<HtsOption | string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);

  const isTDAdminTCD = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  const clientId = useMemo(() => {
    if (isTDAdminTCD) {
      return selectedTenant?._id;
    }
    if (selectedClient?._id) {
      return selectedClient._id;
    }
    if (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT) {
      return user?.tenantId;
    }
    return undefined;
  }, [isTDAdminTCD, selectedTenant, selectedClient, user]);

  const { data: clientHtsCodes, isLoading: isLoadingClientCodes } = useGetHTSCodesQuery(
    { clientId: clientId || "" },
    { skip: !clientId }
  );

  const sortedClientHtsCodes: ClientHtsOption[] = useMemo(() => {
    if (!clientHtsCodes?.getHTSCodesByCountry) return [];

    return [...clientHtsCodes.getHTSCodesByCountry]
      .sort((a, b) => b.goods_value - a.goods_value)
      .map(code => ({
        ...code,
        formatted_hts_code: formatHTSCode(code.hts_code)
      }));
  }, [clientHtsCodes]);

  const filteredClientCodes = useMemo(() => {
    if (!searchTerm) return sortedClientHtsCodes;
    const normalizedSearch = normalizeHtsCode(searchTerm);
    return sortedClientHtsCodes.filter(code =>
      normalizeHtsCode(code.hts_code).includes(normalizedSearch) ||
      code.formatted_hts_code.includes(searchTerm)
    );
  }, [sortedClientHtsCodes, searchTerm]);

  const selectedHtsCode = typeof htsSearchValue === 'object' && htsSearchValue
    ? htsSearchValue.hts_code
    : (typeof htsSearchValue === 'string' ? htsSearchValue : undefined);

  const selectedHtsCodeForApi = selectedHtsCode?.replace(/\./g, '');

  // Fetch HTS description using context
  const { descriptions, fetchDescription } = useHTSDescriptions();

  useEffect(() => {
    if (selectedHtsCodeForApi) {
      fetchDescription(selectedHtsCodeForApi);
    }
  }, [selectedHtsCodeForApi, fetchDescription]);

  const htsDescription = descriptions[selectedHtsCodeForApi || ''];

  const { data: unitCostData } = useGetAverageUnitCostQuery({
    input: {
      htsCode: selectedHtsCodeForApi || '',
      clientId,
      includeOptions: false
    }
  }, {
    skip: !selectedHtsCodeForApi
  });

  // Reset HTS selection when client changes
  useEffect(() => {
    setHtsSearchValue(null);
    setSearchTerm('');
  }, [clientId]);

  return (
    <Box sx={{ pt: 3 }}>
      <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid #E2E8F0' }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" fontWeight={600} color="#0F172A" sx={{ mb: 0.5 }}>
              Select HTS Code
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose an HTS code to analyze historical unit costs and identify trends
            </Typography>
          </Box>

          <Box>
            <Autocomplete<AnyHtsOption, false, false, true>
              freeSolo
              options={filteredClientCodes as AnyHtsOption[]}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                if (isClientHtsOption(option)) return option.formatted_hts_code;
                return option.hts_code || '';
              }}
              filterOptions={(x) => x}
              loading={isLoadingClientCodes}
              value={htsSearchValue as AnyHtsOption | string | null}
              inputValue={searchTerm}
              onInputChange={(_, newInputValue, reason) => {
                if (reason !== 'reset') {
                  setSearchTerm(newInputValue);
                }
              }}
              onChange={(_, newValue) => {
                if (typeof newValue === 'string') {
                  const cleanCode = newValue.replace(/\./g, '');
                  setHtsSearchValue({ hts_code: formatHTSCode(cleanCode) });
                  setSearchTerm('');
                } else if (newValue && isClientHtsOption(newValue)) {
                  setHtsSearchValue({ hts_code: newValue.formatted_hts_code });
                  setSearchTerm('');
                } else {
                  setHtsSearchValue(newValue);
                  setSearchTerm('');
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Filter your imported HTS codes..."
                  size="small"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#F8FAFC',
                      borderRadius: 2,
                      '&:hover': { bgcolor: '#F1F5F9' },
                      '&.Mui-focused': {
                        bgcolor: '#fff',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0B494B',
                          borderWidth: 2
                        }
                      }
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {isLoadingClientCodes && <CircularProgress color="inherit" size={18} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                const isClientCode = isClientHtsOption(option);
                const htsCode = isClientCode ? option.formatted_hts_code : option.hts_code;
                const rawCode = isClientCode ? option.hts_code : option.hts_code.replace(/\./g, '');

                return (
                  <li {...props} key={rawCode}>
                    <Box sx={{ py: 0.5, width: '100%' }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              bgcolor: '#E8F4F5',
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              fontFamily: 'monospace'
                            }}
                          >
                            {htsCode}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: rawCode.length === 10 ? '#0B494B' : 'text.secondary',
                              fontWeight: rawCode.length === 10 ? 600 : 400
                            }}
                          >
                            {rawCode.length === 10 ? '10-digit' : `${rawCode.length}-digit`}
                          </Typography>
                        </Stack>
                        {isClientCode && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            ${(option.goods_value / 1000000).toFixed(1)}M
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </li>
                );
              }}
              ListboxProps={{
                sx: {
                  maxHeight: 400,
                  p: 0,
                  '& .MuiAutocomplete-option': {
                    borderBottom: '1px solid #F1F5F9',
                    '&:last-child': { borderBottom: 'none' }
                  }
                }
              }}
              noOptionsText={
                sortedClientHtsCodes.length === 0 ? "No HTS codes found for your imports" : "No matching HTS codes"
              }
            />
          </Box>
        </Stack>
      </Paper>

      {selectedHtsCodeForApi && (
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FAFBFC' }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={600}>
                Analyzing HTS Code
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 0.5, minWidth: 0 }}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{
                    fontFamily: 'monospace',
                    color: '#0B494B',
                    bgcolor: '#E8F4F5',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1.5,
                    flexShrink: 0
                  }}
                >
                  {selectedHtsCode}
                </Typography>
                {htsDescription?.isLoading ? (
                  <CircularProgress size={16} sx={{ color: '#0B494B' }} />
                ) : htsDescription?.description ? (
                  <Tooltip title={htsDescription.description} placement="top" arrow>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {htsDescription.description}
                    </Typography>
                  </Tooltip>
                ) : null}
              </Stack>
            </Box>

            {unitCostData?.data && (
              <Stack direction="row" spacing={3} sx={{ pt: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Avg Unit Cost
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="#0B494B">
                    ${formatNumberWithSuffix(unitCostData.data.averageUnitCost)}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Total Quantity
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {formatNumberWithSuffix(unitCostData.data.totalQuantity)}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Total Value
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    ${formatNumberWithSuffix(unitCostData.data.totalGoodsValue)}
                  </Typography>
                </Box>
              </Stack>
            )}
          </Stack>
        </Paper>
      )}

      {selectedHtsCodeForApi ? (
        <UnitCostAnalysisSection
          htsCode={selectedHtsCodeForApi}
          clientId={clientId}
        />
      ) : (
        <PlaceholderCard
          title="Select an HTS Code to Begin"
          description="Use the search field above to find an HTS code and view detailed unit cost analysis including historical trends, manufacturer data, and origin breakdowns."
          imgUrl={tariff_code_placeholder}
          centerContent
        />
      )}
    </Box>
  );
};

export default UnitCostAnalysisView;
